import axios, { AxiosRequestConfig as _AxiosRequestConfig, Method } from 'axios';
import { Toast } from 'antd-mobile';
import { pathToRegexp } from 'path-to-regexp';
import { queryURL } from '@utils/index';
import { HTTP_ERROR } from '@constants/index';

export interface AxiosRequestConfig extends _AxiosRequestConfig {
    startTime?: Date;
}

export interface HttpRequest {
    get<T>(url: string, data: any, baseUrl?: string): Promise<T>;
    post<T>(url: string, data: any, baseUrl?: string): Promise<T>;
    delete<T>(url: string, data: any, baseUrl?: string): Promise<T>;
    put<T>(url: string, data: any, baseUrl?: string): Promise<T>;
}

const TOKEN_ERROR = [401, 402, 403];

const DEFAULT_CONFIG = {
    baseURL: process.env.BASE_URL
};

// @ts-ignore
const http: HttpRequest = {};
const methods: Method[] = ['get', 'post', 'put', 'delete'];

const isSuccess = (res: any) => res.response || res.errCode === 0;
const resFormat = (res: any) => res.response || res.data || res.result || {};

methods.forEach((v: Method) => {
    // @ts-ignore
    http[v] = (url: string, data: any, baseUrl?: string) => {
        const axiosConfig: AxiosRequestConfig = {
            method: v,
            url,
            baseURL: baseUrl || DEFAULT_CONFIG.baseURL,
            headers: {
                Authorization: `${queryURL('token')}`,
                'Content-Type':
                    url.indexOf('/speech_recognize') !== -1 ? 'application/octet-stream' : 'application/json'
            }
        };

        const instance = axios.create(DEFAULT_CONFIG);

        instance.interceptors.request.use(
            cfg => {
                cfg.params = { ...cfg.params, ts: Date.now() / 1000, v: process.env.APP_VERSION };
                return cfg;
            },
            error => Promise.reject(error)
        );

        instance.interceptors.response.use(
            response => {
                const rData =
                    response.data === 'object' && !isNaN(response.data.length) ? response.data[0] : response.data;
                if (!isSuccess(rData)) {
                    return Promise.reject({
                        msg: rData.errInfo,
                        action: rData.action,
                        errCode: rData.errCode,
                        type: HTTP_ERROR[HTTP_ERROR.LOGIC_ERROR],
                        config: response.config
                    });
                }

                if (url.endsWith('/face')) {
                    // @ts-ignore
                    rData['result']['action'] = rData.action;
                }
                return resFormat(rData);
            },
            error => {
                if (TOKEN_ERROR.includes(error.response.status)) {
                    Toast.clear();
                    Toast.show({
                        content: '链接失效',
                        duration: 3000,
                        afterClose: () => {
                            const urlObj = new URL(window.location.href);
                            if (!pathToRegexp('/invalid').exec(urlObj.pathname)) {
                                window.location.replace('/invalid');
                            }
                        }
                    });
                }

                return Promise.reject({
                    msg: error.response.statusText || error.message || '服务器开小差了～～～',
                    type: /^timeout of/.test(error.message)
                        ? HTTP_ERROR[HTTP_ERROR.TIMEOUT_ERROR]
                        : HTTP_ERROR[HTTP_ERROR.NETWORK_ERROR],
                    config: error.config
                });
            }
        );

        if (v === 'get') {
            axiosConfig.params = data;
        } else {
            axiosConfig.data = data;
        }

        axiosConfig.startTime = new Date();

        return instance
            .request(axiosConfig)
            .then(res => res)
            .catch(error => {
                if (
                    error.config.url !== '/verify/task/failed' &&
                    error.config.url.indexOf('/person') !== -1 &&
                    error.config.url.indexOf('/face') !== -1
                ) {
                    Toast.clear();
                    Toast.show({
                        content:
                            error.type === HTTP_ERROR[HTTP_ERROR.NETWORK_ERROR]
                                ? '网络错误'
                                : error.response || error.msg || error.stack || '服务器开小差了～～～',
                        duration: 3000
                    });
                }

                if (error.config.url === '/verify/task') {
                    Toast.clear();
                    Toast.show({
                        content:
                            error.type === HTTP_ERROR[HTTP_ERROR.NETWORK_ERROR]
                                ? '网络错误'
                                : error.response || error.msg || error.stack || '服务器开小差了～～～',
                        duration: 3000
                    });
                }

                if (error.config.url.indexOf('/openapi') !== -1) {
                    Toast.clear();
                    Toast.show({
                        content:
                            error.type === HTTP_ERROR[HTTP_ERROR.NETWORK_ERROR]
                                ? '网络错误'
                                : error.response || error.msg || error.stack || '服务器开小差了～～～',
                        duration: 3000
                    });
                }
                return Promise.reject(
                    axiosConfig.url?.includes('autoScript.set')
                        ? { error }
                        : { error, stack: error.msg || error.stack || '' }
                );
            });
    };
});

export default http;
